import {
  Input,
  Tab,
  Tabs,
} from '@heroui/react'
import { TopBar } from 'components/Layout/TopBar'
import { TopBarContent } from 'components/Layout/TopBarProvider'

import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import {
  useKnowledgeBases,
} from 'hooks/useKnowledgeBase'
import {
  Search,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { KnowledgeBaseCards, Playground, Suggestions, UploadSidebar } from './KnowledgeBases/index.js'
import MemoryStores from './MemoryStores/MemStore'

export default function KnowledgeBases() {
  const [selectedTab, setSelectedTab] = useState('knowledgeBases')
  const [activeTab, setActiveTab] = useState('files')
  const [globalFilter, setGlobalFilter] = useState('')
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null)

  const navigate = useNavigate()
  const location = useLocation()

  // Use the custom hooks
  const { data: knowledgeBasesData, isLoading: loading, refetch: fetchKnowledgeBases } = useKnowledgeBases({}, {
    staleTime: 0,
    cacheTime: 0,
    refetchInterval: (data) => {
      const hasProcessingItems = data?.data?.kbs?.some(kb => kb.status === 'PROCESSING')
      return hasProcessingItems ? 3000 : false
    },
  })

  // Transform data to match existing structure
  const knowledgeBases = knowledgeBasesData?.data
    ? {
        vectors: knowledgeBasesData.data.kbs || [],
        total: knowledgeBasesData.data.total || 0,
      }
    : { vectors: [] }

  // Function to switch to playground with a selected knowledge base
  const goToPlayground = (knowledgeBase) => {
    setSelectedKnowledgeBase(knowledgeBase)
    setActiveTab('playground')
  }

  // Check URL path to set initial tab
  const updateTabFromPath = useCallback(() => {
    const newTab = location.pathname.includes('/memory') ? 'memoryStores' : 'knowledgeBases'
    if (selectedTab !== newTab) {
      setSelectedTab(newTab)
    }
  }, [location.pathname, selectedTab])

  
  useEffect(() => {
    updateTabFromPath()
  }, [updateTabFromPath])

  return (
    <div className="h-full w-full flex-1">
      <TopBarContent>
        <TopBar.Title>{selectedTab === 'knowledgeBases' ? 'Knowledge Bases' : 'Memory Stores'}</TopBar.Title>
        <TopBar.Tabs
          selectedKey={selectedTab}
          onSelectionChange={(key) => {
            setSelectedTab(key)
            if (key === 'memoryStores') {
              navigate('/dashboard/memory')
            }
            else {
              navigate('/dashboard/knowledge-bases')
            }
          }}
        >
          <TopBar.Tabs.List>
            <TopBar.Tabs.Tab key="knowledgeBases" id="knowledgeBases">
              Knowledge Bases
            </TopBar.Tabs.Tab>
            <TopBar.Tabs.Tab key="memoryStores" id="memoryStores">
              Memory Store
            </TopBar.Tabs.Tab>
          </TopBar.Tabs.List>
        </TopBar.Tabs>
        <TopBar.Actions />
      </TopBarContent>

      <div className="flex h-full bg-gray-50">
        {/* Main Content */}
        {selectedTab === 'knowledgeBases' && (
          loading
            ? (
                <div className="flex flex-1 items-center justify-center">
                  <GradientLoadingAnimation
                    message="Loading Knowledge Bases"
                    variant="roseGarden"
                    rounded="full"
                  />
                </div>
              )
            : (
                <div className="flex flex-1 flex-col overflow-hidden">
                  {/* Tabs */}
                  <div className="flex-shrink-0 px-4 pt-4">
                    <div className="mb-4 flex items-center justify-between">
                      <Tabs
                        selectedKey={activeTab}
                        onSelectionChange={setActiveTab}
                        variant="underlined"
                        radius="none"
                      >
                        <Tab key="files" title="Files" />
                        <Tab key="analytics" title="Analytics (Coming Soon)" isDisabled />
                        <Tab key="suggestions" title="Suggestions" />
                        <Tab key="playground" title="Playground" />
                      </Tabs>

                      {/* Search Section */}
                      <div className="relative w-80">
                        <Search className={`
                          absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2
                          text-gray-400
                        `}
                        />
                        <Input
                          placeholder="Search Knowledge base"
                          className="border-gray-200 bg-gray-50 pl-10"
                          value={globalFilter ?? ''}
                          onChange={event => setGlobalFilter(event.target.value)}
                          radius="none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Scrollable Content Area */}
                  <div className="flex-1 overflow-y-auto px-4 pb-4">
                    {activeTab === 'files' && (
                      <KnowledgeBaseCards
                        data={knowledgeBases}
                        onRefresh={fetchKnowledgeBases}
                        globalFilter={globalFilter}
                        onTestKnowledgeBase={goToPlayground}
                      />
                    )}

                    {activeTab === 'suggestions' && (
                      <Suggestions
                        knowledgeBases={knowledgeBases}
                      />
                    )}

                    {activeTab === 'playground' && (
                      <Playground
                        knowledgeBases={knowledgeBases}
                        selectedKnowledgeBase={selectedKnowledgeBase}
                      />
                    )}
                  </div>
                </div>
              )
        )}

        {selectedTab === 'memoryStores' && (
          <div className="flex-1 overflow-hidden">
            <MemoryStores />
          </div>
        )}

        {selectedTab === 'knowledgeBases' && !loading && activeTab === 'files' && (
          <UploadSidebar onSuccess={fetchKnowledgeBases} />
        )}
      </div>
    </div>
  )
}
